<template>
    <div>
        <div v-if="!fetchingPoolInvitations">
            <div class="d-flex flex-wrap align-center">
                <div v-if="isResourcePoolManager">
                    <ResourcePoolInviteDialog @invited="fetchPoolInvitations">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn color="primary" v-bind="attrs" v-on="on">+ manager</v-btn>
                        </template>
                    </ResourcePoolInviteDialog>
                </div>
                <v-spacer />
                <v-text-field
                    v-model="invitationEmailSearch"
                    autofocus
                    placeholder="Search for an invitation..."
                    prepend-inner-icon="mdi-magnify "
                    solo
                    flat
                    dense
                    hide-details
                    clearable>
                    <template v-slot:append>
                        <v-menu offset-y :close-on-content-click="false">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn v-bind="attrs" v-on="on" small class="mr-n2">
                                    <v-icon left>mdi-tune-variant</v-icon>
                                    <span>Filters</span>
                                </v-btn>
                            </template>
                            <v-list nav dense class="d-flex align-start">
                                <div class="filter-section">
                                    <v-subheader>STATUS</v-subheader>
                                    <div v-for="invitationType in availableInvitationTypes" :key="invitationType">
                                        <v-sheet rounded class="py-2 px-4 my-1">
                                            <v-switch v-model="invitationsSwitchData[invitationType]" inset dense hide-details class="pa-0 ma-0">
                                                <template v-slot:label>
                                                    <div class="d-flex align-center">
                                                        <v-icon left small :color="getMailIconColor(invitationType)">mdi-email-outline</v-icon>
                                                        <span>{{ capitalize(invitationAndRequestStatusTypes[invitationType]) }}</span>
                                                    </div>
                                                </template>
                                            </v-switch>
                                        </v-sheet>
                                    </div>
                                </div>
                                <v-divider vertical class="mx-2 align-self-stretch" />
                                <div class="filter-section">
                                    <v-subheader>ROLE</v-subheader>
                                    <div v-for="role in availableRoles" :key="role">
                                        <v-sheet rounded class="py-2 px-4 my-1">
                                            <v-switch
                                                :input-value="roleFilters[role] !== false"
                                                @change="$set(roleFilters, role, $event)"
                                                inset
                                                dense
                                                hide-details
                                                class="pa-0 ma-0">
                                                <template v-slot:label>
                                                    <div class="d-flex align-center">
                                                        <v-icon left small>{{ resourcePoolRoleMetadata(role).icon }}</v-icon>
                                                        <span>{{ capitalize(resourcePoolRoleMetadata(role).label) }}</span>
                                                    </div>
                                                </template>
                                            </v-switch>
                                        </v-sheet>
                                    </div>
                                </div>
                            </v-list>
                        </v-menu>
                    </template>
                </v-text-field>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn class="ml-2" v-on="on" @click="fetchPoolInvitations" text icon>
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>refresh</span>
                </v-tooltip>
            </div>
            <v-data-table
                :headers="invitationHeaders"
                :items="filteredInvitationsList"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                item-key="invid"
                class="mt-4">
                <template v-slot:top="{ options, pagination, updateOptions }">
                    <div class="d-flex align-center">
                        <v-spacer></v-spacer>
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </div>
                </template>
                <template v-slot:[`item.invitation`]="{ item }">
                    <div class="d-flex flex-column py-2">
                        <div>{{ item.requestee_email || '-' }}</div>
                    </div>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <v-chip outlined label small>
                        <v-icon left small :color="getMailIconColor(getInvitationStatus(item))">mdi-email-outline</v-icon>
                        {{ capitalize(getInvitationStatus(item)) }}
                    </v-chip>
                </template>
                <template v-slot:[`item.roles`]="{ item }">
                    <v-chip v-for="(role, index) in resourcePoolRoles(item)" :key="`${item.invid}-${index}`" outlined label small>
                        <v-icon left small>{{ resourcePoolRoleMetadata(role.Name).icon }}</v-icon>
                        {{ capitalize(resourcePoolRoleMetadata(role.Name).label) }}
                    </v-chip>
                </template>
                <template v-slot:[`item.sent_on`]="{ item }">
                    <div class="py-2 text-no-wrap">
                        <span v-if="item.notification_timestamp">{{ item.notification_timestamp | dateTimeToHuman }}</span>
                        <span v-else>-</span>
                    </div>
                </template>
                <template v-slot:[`item.accepted_on`]="{ item }">
                    <div class="py-2 text-no-wrap">
                        <span v-if="getInvitationResponseTimestamp(item, invitationAndRequestStatusTypes.ACCEPTED)">
                            {{ getInvitationResponseTimestamp(item, invitationAndRequestStatusTypes.ACCEPTED) | dateTimeToHuman }}
                        </span>
                        <span v-else>-</span>
                    </div>
                </template>
                <template v-slot:[`item.rejected_on`]="{ item }">
                    <div class="py-2 text-no-wrap">
                        <span v-if="getInvitationResponseTimestamp(item, invitationAndRequestStatusTypes.REJECTED)">
                            {{ getInvitationResponseTimestamp(item, invitationAndRequestStatusTypes.REJECTED) | dateTimeToHuman }}
                        </span>
                        <span v-else>-</span>
                    </div>
                </template>
                <template v-slot:[`item.revoked_on`]="{ item }">
                    <div class="py-2 text-no-wrap">
                        <span v-if="item.revocation_timestamp">{{ item.revocation_timestamp | dateTimeToHuman }}</span>
                        <span v-else>-</span>
                    </div>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu
                        left
                        offset-x
                        nudge-top="10"
                        v-if="
                            isResourcePoolManager &&
                            (getInvitationStatus(item) === invitationAndRequestStatusTypes.PENDING ||
                                getInvitationStatus(item) === invitationAndRequestStatusTypes.CREATED ||
                                item.status_name === 'SEND_FAILED')
                        ">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <InvitationActionsDialog
                                activatorType="listItem"
                                :successFetchAction="fetchPoolInvitations"
                                :invitationInfo="item"
                                :actionType="actionTypes.RESEND_INVITATION"></InvitationActionsDialog>
                            <copy-to-clipboard :textToCopy="item.link" buttonName="Copy invitation link" type="listItem"></copy-to-clipboard>
                            <v-divider class="my-2"></v-divider>
                            <InvitationActionsDialog
                                activatorType="listItem"
                                :successFetchAction="fetchPoolInvitations"
                                :invitationInfo="item"
                                :actionType="actionTypes.REVOKE_INVITATION"></InvitationActionsDialog>
                        </v-list>
                    </v-menu>
                </template>
                <template v-slot:no-data>
                    <div class="d-flex flex-column align-center py-8">
                        <v-icon color="info" class="mb-2">mdi-email-off-outline</v-icon>
                        <span class="font-weight-medium secondary--text mb-1">No invitations found</span>
                        <span class="text-center">This resource pool has no invitation history yet.</span>
                    </div>
                </template>
                <template v-slot:no-results>
                    <div class="d-flex flex-column align-center py-8">
                        <v-icon color="info" class="mb-2">mdi-magnify-close</v-icon>
                        <span>No invitations found for the current filters.</span>
                    </div>
                </template>
            </v-data-table>
        </div>
        <div v-else-if="fetchingPoolInvitations">
            <div
                v-for="item in [
                    { id: 'c1', opacity: 1 },
                    { id: 'c2', opacity: 0.75 },
                    { id: 'c3', opacity: 0.5 }
                ]"
                :key="item.id"
                class="my-6">
                <div :style="{ opacity: item.opacity }">
                    <v-skeleton-loader type="list-item-avatar-two-line" class="mx-auto" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { resourcePoolRoleMetadata } from '@/modules/user/components/resourcePoolMeta'
import { mapGetters, mapState } from 'vuex'
import { invitationsAndRequests } from '@/mixins/invitationsAndRequests'
import { capitalize, sortArray } from '@/utils'
import ResourcePoolInviteDialog from '@/modules/user/components/ResourcePoolInviteDialog.vue'

const CopyToClipboard = () => import('@/components/CopyToClipboard')
const InvitationActionsDialog = () => import('@/modules/invitations/components/InvitationsActionsDialog')

const createDefaultInvitationsSwitchData = () => ({
    ACCEPTED: true,
    PENDING: true,
    REJECTED: true,
    REVOKED: true,
    EXPIRED: true,
    BROKEN: true,
    CREATED: true
})

const createDefaultRoleFilters = () => ({
    MANAGER: true
})

export default {
    name: 'PoolUserInvitations',
    mixins: [invitationsAndRequests],
    components: {
        CopyToClipboard,
        InvitationActionsDialog,
        ResourcePoolInviteDialog
    },
    data() {
        return {
            poolInvitations: [],
            fetchingPoolInvitations: false,
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, -1],
            invitationsSwitchData: createDefaultInvitationsSwitchData(),
            roleFilters: createDefaultRoleFilters(),
            invitationEmailSearch: ''
        }
    },
    computed: {
        ...mapGetters('userStore', ['isResourcePoolManager']),
        ...mapState('userStore', ['selectedResourcePool', 'availableResourcePools']),
        activeResourcePoolId() {
            const routePoolId = Number(this.$route.params.rpid)
            if (Number.isFinite(routePoolId) && routePoolId > 0) {
                return routePoolId
            }

            const selectedPoolId = Number(this.selectedResourcePool)
            if (Number.isFinite(selectedPoolId) && selectedPoolId > 0) {
                return selectedPoolId
            }

            return Number(this.availableResourcePools[0]?.rpid || 0)
        },
        invitationHeaders() {
            const headers = [
                { text: 'Invitation', value: 'invitation', sortable: false },
                { text: 'Status', value: 'status', sortable: false, width: '140px' },
                { text: 'Role', value: 'roles', sortable: false },
                { text: 'Sent on', value: 'sent_on', sortable: false, width: '180px' },
                { text: 'Accepted on', value: 'accepted_on', sortable: false, width: '180px' },
                { text: 'Rejected on', value: 'rejected_on', sortable: false, width: '180px' },
                { text: 'Revoked on', value: 'revoked_on', sortable: false, width: '180px' }
            ]
            if (this.isResourcePoolManager) {
                headers.push({ text: 'Actions', value: 'actions', align: 'right', sortable: false, width: '100px' })
            }
            return headers
        },
        availableInvitationTypes() {
            const invitationTypes = []
            if (this.poolInvitations && this.poolInvitations.length) {
                this.poolInvitations.forEach(invitation => {
                    const invitationType = this.getInvitationStatus(invitation)
                    if (!invitationTypes.includes(invitationType)) {
                        invitationTypes.push(invitationType)
                    }
                })
            }
            return invitationTypes
        },
        displayedInvitationTypes() {
            return Object.values(this.invitationAndRequestStatusTypes).filter(
                value => this.availableInvitationTypes.includes(value) && this.invitationsSwitchData[value]
            )
        },
        availableRoles() {
            return [...new Set(this.poolInvitations.flatMap(invitation => this.resourcePoolRoles(invitation).map(role => role.Name)))].filter(Boolean)
        },
        filteredInvitationsList() {
            const term = this.invitationEmailSearch?.trim().toLowerCase()
            const invitations = this.poolInvitations.filter(invitation => {
                const matchesStatus = this.displayedInvitationTypes.includes(this.getInvitationStatus(invitation))
                const matchesRole = this.resourcePoolRoles(invitation).some(role => this.roleFilters[role.Name] !== false)
                const matchesSearch = !term || invitation.requestee_email?.toLowerCase().includes(term)
                return matchesStatus && matchesRole && matchesSearch
            })
            return sortArray(invitations, 'invid', 'descending', false)
        }
    },
    methods: {
        capitalize,
        resourcePoolRoleMetadata,
        getInvitationResponseTimestamp(invitation, responseType) {
            const responses = invitation?.invitation_responses || []
            return responses.find(response => response.response === responseType)?.timestamp || null
        },
        resetInvitationFilters() {
            this.invitationsSwitchData = createDefaultInvitationsSwitchData()
            this.roleFilters = createDefaultRoleFilters()
        },
        resourcePoolRoles(invitation) {
            return (invitation.roles || []).filter(role => role.role_type === this.invitationAndRequestRoleTypes.RESOURCEPOOL_ROLE)
        },
        async fetchPoolInvitations() {
            if (!this.activeResourcePoolId) {
                this.poolInvitations = []
                return
            }

            this.resetInvitationFilters()
            this.fetchingPoolInvitations = true

            this.poolInvitations = []

            try {
                const { data } = await this.$axios.post(`/resources/${this.activeResourcePoolId}/invitations`, {})
                if (Array.isArray(data)) {
                    this.poolInvitations = data
                }
            } catch (error) {
                this.poolInvitations = []
            }

            this.fetchingPoolInvitations = false
        }
    },
    watch: {
        activeResourcePoolId: {
            handler() {
                this.fetchPoolInvitations()
            },
            immediate: true
        }
    }
}
</script>

<style scoped>
.filter-section {
    min-width: 190px;
}
</style>
